import "./style.scss";

import { useAuthenticator } from "@aws-amplify/ui-react";
import Plus from "assets/img/svg/plus";
import AddChannelButton from "components/shared/AddChannelButton";
import StyledButton from "components/shared/StyledButton";
import TextButton from "components/shared/TextButton";
import { getTemplateChannels } from "components/TermTemplates/utils";
import { CalculationMethod } from "containers/Channels";
import type { IChannel } from "containers/Channels/types";
import { getCognitoUserAttribute, sortArrayByBooleanProperty } from "helpers/utils";
import { useAbility } from "hooks/useAbility";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useState } from "react";

import TermCustomFields from "../TermCustomFields";
import { useTranslation } from "react-i18next";
import type { CalculationBaseColumn } from "store/calculationBaseColumnLabelsStore";
import { getCalculationBaseColumnLabels } from "store/calculationBaseColumnLabelsStore";
import type { ChannelCompensationGroup } from "store/channelStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TermTemplate } from "store/termTemplatesStore";

import type { ChannelParams, GroupParams } from "..";
import { ITermFormData } from "..";
import {
    alignCompensationGroupFormulasOnSync,
    getGroupWithChannels,
    shouldSetPublishedBeforePeriodFromOpeningBalance,
} from "../utils";
import { getDefaultCalculationBaseColumn } from "./calculationBaseColumnUtils";
import { resolveMilestoneBaseColumn } from "./milestoneBaseColumnUtils";
import ChannelsListSection from "./ChannelsListSection";
import GroupsListSection from "./GroupsListSection";
import { useContractChannelLists } from "./hooks/useContractChannelLists";
import { useFrozenChannelDisplayPartition } from "./hooks/useFrozenChannelDisplayPartition";
import TemplatesModal from "./TemplatesModal";
import type { GroupWithChannels } from "./types";

export type { GroupWithChannels } from "./types";

interface ChannelsStep {
    /**
     * index of the current step.
     */
    indexStep?: number;
    /**
     * title of the step.
     */
    stepTitle?: string;
    /**
     * subtitle of the step.
     */
    stepSubtitle?: string;
    /**
     * all channels of the company.
     */
    channels: IChannel[];
    /**
     * channels that aren't within a compensation group
     */
    filteredChannels: IChannel[];
    /**
     * channels active on this title (comes from store).
     */
    activedChannels?: { channelId: number }[];
    /**
     * (useState) array that contains channels chosen within the form and their data.
     */
    chosenChannels: ChannelParams[];
    /**
     * (useState) to save the chosenChannels.
     */
    setChosenChannels: Dispatch<SetStateAction<ChannelParams[]>>;
    /**
     * (useState) (useState) array with only ids of the channels chosen.
     */
    activatedChannels: number[];
    /**
     * (useState) (useState) to save activatedChannels.
     */
    setActivatedChannels: Dispatch<SetStateAction<number[]>>;
    /**
     * triggers opening modal to create new channel.
     */
    onAddChannel: (value: boolean) => void;
    /**
     * whether it is the creation form or edit one.
     */
    mode: "new" | "edit";
    /**
     * Triggers save button on edit form.
     */
    onChange?: VoidFunction;
    /**
     * Compensation groups with channels associated (and only those that have channels)
     */
    groupsWithChannels: GroupWithChannels[];
    /**
     * (useState) (useState) array with only ids of the groups chosen.
     */
    activeGroups: number[];
    /**
     * (useState) (useState) to save activeGroups.
     */
    setActiveGroups: Dispatch<SetStateAction<number[]>>;
    /**
     * (useState) array that contains groups chosen within the form and their data.
     */
    chosenGroups: GroupParams[];
    /**
     * (useState) to save the chosenGroups.
     */
    setChosenGroups: Dispatch<SetStateAction<GroupParams[]>>;
    /**
     * Whether company uses groups of channels for compensation.
     */
    usesCompensation: boolean;
    /**
     * Are we accessing component from the term form or the template form.
     */
    from: "templates" | "form";
    /**
     * Existing froups of channels (names and ids)
     */
    channelCompensationGroups?: ChannelCompensationGroup[];
    /**
     * Whether they have used template to fill up form.
     */
    usesTemplate?: boolean;
    /**
     * Updates value of usesTemplate.
     */
    setUsesTemplate?: Dispatch<SetStateAction<boolean>>;
    /**
     * Templates created by company.
     */
    termTemplates?: TermTemplate[];
    /**
     * Framework contracts (term_templates with beneficiaryId) of the currently selected beneficiary.
     */
    frameworkContracts?: TermTemplate[];
    useFormData: any; // Clearly not best typing but merging two forms types was causing error. As this needs to be fixed quickly, I'm leaving this for now. And will improve typing in dev.
    /** When false in edit mode, channel list partition is not frozen yet (term data loading). */
    isEditChannelsReady?: boolean;
    /** Format types the formula can override: those on the work, or every company type on a template. */
    formatTypesOnWork?: { id: number; name: string }[];
}

/**
 * This component handles the second section of the Term Form dealing with the selection of channels.
 */
const ChannelsStep = ({
    indexStep,
    stepTitle,
    stepSubtitle,
    channels,
    filteredChannels,
    activedChannels,
    chosenChannels,
    setChosenChannels,
    onAddChannel,
    activatedChannels,
    setActivatedChannels,
    mode,
    onChange,
    activeGroups,
    setActiveGroups,
    chosenGroups,
    setChosenGroups,
    usesCompensation,
    groupsWithChannels,
    from,
    channelCompensationGroups,
    usesTemplate,
    setUsesTemplate,
    termTemplates,
    frameworkContracts,
    useFormData,
    isEditChannelsReady = true,
    formatTypesOnWork = [],
}: ChannelsStep) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { labels: calculationBaseColumnLabels, labelsStatus } = useAppSelector(
        (state) => state.calculationBaseColumnLabels,
    );
    const { getCompanySettings } = useCompanySettings();
    const { user } = useAuthenticator((context) => [context.user]);
    const ability = useAbility();
    const { setError, setValue, clearErrors } = useFormData;

    const accountType = getCognitoUserAttribute("custom:accountType", user);
    const usesAgreementsOnly = getCompanySettings("uses_agreements_only");

    const hasCreateChannelPermission =
        !usesAgreementsOnly ||
        (ability.can("create", "Channel") && ability.can("update", "CompanySetting")) ||
        accountType === "admin";

    const [modalType, setModalType] = useState("");
    const [orderedChannels, setOrderedChannels] = useState(filteredChannels);
    const [orderedGroups, setOrderedGroups] = useState(groupsWithChannels);
    const { displayPartitionChannelIds, displayPartitionGroupIds } = useFrozenChannelDisplayPartition(
        mode,
        isEditChannelsReady,
        chosenChannels,
        activeGroups,
    );
    const { activeChannelsList, inactiveChannelsList, activeGroupsList, inactiveGroupsList } = useContractChannelLists(
        mode,
        orderedChannels,
        chosenChannels,
        orderedGroups,
        activeGroups,
        {
            channelIds: displayPartitionChannelIds,
            groupIds: displayPartitionGroupIds,
        },
    );

    useEffect(() => {
        if (labelsStatus === "idle") {
            dispatch(getCalculationBaseColumnLabels());
        }
    }, [labelsStatus, dispatch]);

    // Update orderedChannels state when filteredChannels change ex : after create a channel
    useEffect(() => {
        setOrderedChannels(filteredChannels);
    }, [filteredChannels]);

    /**
     * Handles saving the changes made in the inputs of the milestones (either replacing or storing from scratch)
     * @param newChannel Channel and its data.
     * @param groupId (optional) Id of the group in which it exists.
     */
    const handleChannelUpdates = (newChannel: any, groupId?: number) => {
        const sameFormulaRow = (channel: ChannelParams) =>
            channel.channelId === newChannel.channelId &&
            (channel.formatTypeId ?? null) === (newChannel.formatTypeId ?? null);
        const channelOnContract = chosenChannels?.some(sameFormulaRow);
        let newChosenChannels = chosenChannels;

        // MilestonesInput only sends { channelId, data }: merge into the existing channel so
        // calculationBaseColumn / milestoneBaseColumn (and other term fields) are not wiped.
        newChosenChannels = chosenChannels?.map((channel) => {
            if (sameFormulaRow(channel)) {
                const fixedData = newChannel.data.map((milestone: any) => {
                    return {
                        ...milestone,
                        percentage: milestone.percentage === "" ? 0 : +milestone.percentage,
                        quantity: milestone.quantity === "" ? 0 : +milestone.quantity,
                    };
                });
                return {
                    ...channel,
                    data: fixedData,
                    groupId: groupId ?? channel.groupId,
                };
            } else {
                return channel;
            }
        });

        // If channel doesn't exist add it.
        newChosenChannels = !channelOnContract ? [...newChosenChannels, newChannel] : newChosenChannels;
        setChosenChannels(newChosenChannels);

        onChange && onChange();
    };

    const setDefaultConfigurationVisibility = (channelIds: Set<number>, showDefaultConfiguration: boolean) => {
        setChosenChannels((prev) =>
            prev.map((row) =>
                channelIds.has(row.channelId) && row.formatTypeId == null ? { ...row, showDefaultConfiguration } : row,
            ),
        );
        if (!showDefaultConfiguration) clearErrors?.("channels");
        onChange && onChange();
    };

    const handleShowDefaultConfigurationChange = (channelId: number, showDefaultConfiguration: boolean) => {
        setDefaultConfigurationVisibility(new Set([channelId]), showDefaultConfiguration);
    };

    const handleGroupShowDefaultConfigurationChange = (group: GroupWithChannels, showDefaultConfiguration: boolean) => {
        const proportionalIds = new Set(
            group.channels
                .filter((channel) => channel.calculationMethod === CalculationMethod.PROPORTIONAL)
                .map((channel) => channel.id),
        );
        setDefaultConfigurationVisibility(proportionalIds, showDefaultConfiguration);
    };

    const handleToggleFormatTypeOverride = (channelId: number, formatTypeId: number, enabled: boolean) => {
        if (!enabled) {
            setChosenChannels((prev) =>
                prev.filter((row) => !(row.channelId === channelId && row.formatTypeId === formatTypeId)),
            );
            onChange && onChange();
            return;
        }
        if (chosenChannels.some((row) => row.channelId === channelId && row.formatTypeId === formatTypeId)) return;
        const defaultRow = chosenChannels.find((row) => row.channelId === channelId && row.formatTypeId == null);
        const companyChannel = channels.find((item) => item.id === channelId);
        setChosenChannels((prev) => [
            ...prev,
            {
                channelId,
                formatTypeId,
                calculationBaseColumn:
                    defaultRow?.calculationBaseColumn ??
                    (companyChannel ? getDefaultCalculationBaseColumn(companyChannel) : "quantity"),
                milestoneBaseColumn:
                    defaultRow?.milestoneBaseColumn ??
                    (companyChannel ? getDefaultCalculationBaseColumn(companyChannel) : "quantity"),
                data: defaultRow?.data ?? [{ percentage: "", start: 0, quantity: Infinity }],
                groupId: defaultRow?.groupId,
            },
        ]);
        onChange && onChange();
    };

    const handleCalculationBaseColumnChange = (
        channelId: number,
        calculationBaseColumn: CalculationBaseColumn,
        formatTypeId: number | null = null,
    ) => {
        setChosenChannels((prevChannels) =>
            prevChannels.map((channel) => {
                if (channel.channelId !== channelId || (channel.formatTypeId ?? null) !== (formatTypeId ?? null)) {
                    return channel;
                }

                return {
                    ...channel,
                    calculationBaseColumn,
                    // Keep milestone compatible with the new base (lock to quantity, or drop a stale CA column).
                    milestoneBaseColumn: resolveMilestoneBaseColumn(channel.milestoneBaseColumn, calculationBaseColumn),
                };
            }),
        );
        onChange && onChange();
    };

    const handleMilestoneColumnChange = (
        channelId: number,
        milestoneBaseColumn: CalculationBaseColumn,
        formatTypeId: number | null = null,
    ) => {
        setChosenChannels((prevChannels) =>
            prevChannels.map((channel) =>
                channel.channelId === channelId && (channel.formatTypeId ?? null) === (formatTypeId ?? null)
                    ? { ...channel, milestoneBaseColumn }
                    : channel,
            ),
        );
        onChange && onChange();
    };

    const isGroupFormulaRow = (channel: ChannelParams, groupId: number, formatTypeId: number | null) =>
        channel.groupId === groupId && (channel.formatTypeId ?? null) === formatTypeId;

    /** Compensation groups share one calculation/milestone base per formula (default or format override). */
    const handleGroupCalculationBaseColumnChange = (
        groupId: number,
        calculationBaseColumn: CalculationBaseColumn,
        formatTypeId: number | null = null,
        applyToEveryFormula = false,
    ) => {
        setChosenChannels((prevChannels) =>
            prevChannels.map((channel) => {
                if (channel.groupId !== groupId) return channel;
                if (!applyToEveryFormula && !isGroupFormulaRow(channel, groupId, formatTypeId)) return channel;

                return {
                    ...channel,
                    calculationBaseColumn,
                    milestoneBaseColumn: resolveMilestoneBaseColumn(channel.milestoneBaseColumn, calculationBaseColumn),
                };
            }),
        );
        onChange && onChange();
    };

    const handleGroupMilestoneColumnChange = (
        groupId: number,
        milestoneBaseColumn: CalculationBaseColumn,
        formatTypeId: number | null = null,
        applyToEveryFormula = false,
    ) => {
        setChosenChannels((prevChannels) =>
            prevChannels.map((channel) => {
                if (channel.groupId !== groupId) return channel;
                if (!applyToEveryFormula && !isGroupFormulaRow(channel, groupId, formatTypeId)) return channel;
                return { ...channel, milestoneBaseColumn };
            }),
        );
        onChange && onChange();
    };

    const handleToggleGroupFormatTypeOverride = (group: GroupWithChannels, formatTypeId: number, enabled: boolean) => {
        const groupChannelIds = new Set(group.channels.map((channel) => channel.id));
        if (!enabled) {
            setChosenChannels((prev) =>
                prev.filter((row) => !(groupChannelIds.has(row.channelId) && row.formatTypeId === formatTypeId)),
            );
            onChange && onChange();
            return;
        }
        setChosenChannels((prev) => {
            const existingKeys = new Set(
                prev
                    .filter((row) => groupChannelIds.has(row.channelId) && row.formatTypeId === formatTypeId)
                    .map((row) => row.channelId),
            );
            const newRows = group.channels
                .filter((channel) => !existingKeys.has(channel.id))
                .map((channel) => {
                    const defaultRow = prev.find((row) => row.channelId === channel.id && row.formatTypeId == null);
                    return {
                        channelId: channel.id,
                        formatTypeId,
                        groupId: group.id,
                        calculationBaseColumn:
                            defaultRow?.calculationBaseColumn ?? getDefaultCalculationBaseColumn(channel),
                        milestoneBaseColumn:
                            defaultRow?.milestoneBaseColumn ?? getDefaultCalculationBaseColumn(channel),
                        data: defaultRow?.data ?? [{ percentage: "", start: 0, quantity: Infinity }],
                        channelName: channel.name,
                    };
                });
            return [...prev, ...newRows];
        });
        onChange && onChange();
    };

    /**
     * Adds or removes a channel from the state whether it is activated or not.
     * @param e React event to get the checked value.
     * @param channelId id of the channel that was activated.
     * @param calculationMethod proportional or package
     */
    const handleChannelActivated = (
        e: React.ChangeEvent<HTMLInputElement>,
        channelId: number,
        calculationMethod: number,
    ) => {
        e.target.checked && setActivatedChannels((prevChannels) => [...prevChannels, channelId]);
        !e.target.checked && setActivatedChannels((prevChannels) => prevChannels.filter((item) => item !== channelId));

        let newChosenChannels = chosenChannels;

        if (!e.target.checked) {
            newChosenChannels = newChosenChannels.filter((channel) => channel.channelId !== channelId);
        } else {
            const channel = channels.find((item) => item.id === channelId);
            newChosenChannels = [
                ...newChosenChannels,
                {
                    channelId: channelId,
                    calculationBaseColumn: channel ? getDefaultCalculationBaseColumn(channel) : "quantity",
                    milestoneBaseColumn: channel ? getDefaultCalculationBaseColumn(channel) : "quantity",
                    data: [
                        {
                            percentage: calculationMethod === CalculationMethod.PACKAGE ? "100" : "",
                            start: 0,
                            quantity: Infinity,
                        },
                    ],
                },
            ];
        }

        setChosenChannels(newChosenChannels);
        onChange && onChange();
    };

    /**
     * Adds or removes a group from the state whether it is activated or not.
     * @param e React event to get the checked value.
     * @param groupId id of the groupd that was activated.
     */
    const handleGroupActivation = (e: React.ChangeEvent<HTMLInputElement>, group: GroupWithChannels) => {
        let newChosenChannels = chosenChannels;
        let newGroups = chosenGroups;

        const channelIds = group.channels.map((channel) => channel.id);

        if (e.target.checked && group) {
            setActiveGroups((prevGroups) => [...prevGroups, group.id as number]);
            const allIds = [activatedChannels, channelIds].flat();
            setActivatedChannels(allIds);

            // Shared bases for the whole compensation group (engine accumulates in one unit).
            const groupDefaultBase = getDefaultCalculationBaseColumn(group.channels[0]);
            newChosenChannels = [
                ...newChosenChannels,
                ...group.channels.map((channel) => ({
                    channelId: channel.id,
                    groupId: group.id,
                    calculationBaseColumn: groupDefaultBase,
                    milestoneBaseColumn: groupDefaultBase,
                    data: [{ percentage: "", start: 0, quantity: Infinity }],
                    channelName: channel.name,
                })),
            ];
            // Add groups with initial
            newGroups = [
                ...newGroups,
                {
                    id: group.id,
                    name: group.name,
                    data: [{ percentage: "", start: 0, quantity: Infinity }],
                    synced: true,
                },
            ];
        } else {
            setActiveGroups((prevGroups) => prevGroups.filter((item) => item !== group.id));
            const filteredIds = activatedChannels.filter((channel) => !channelIds.includes(channel));
            setActivatedChannels(filteredIds);

            newChosenChannels = newChosenChannels.filter((channel) => !channelIds.includes(channel.channelId));
            newGroups = newGroups.filter((item) => item.id !== group.id);
        }

        setChosenChannels(newChosenChannels);
        setChosenGroups(newGroups);
        onChange && onChange();
    };

    /**
     * Saves data changed in inputs of milestones (either replacing or storing from scratch)
     * @param newGroupData Data of the group {id: number, name: string, data: {}}
     * @param groupChannels Array with channels within the group
     */
    const handleGroupUpdate = (newGroupData: any, groupChannels: IChannel[], formatTypeId: number | null = null) => {
        const groupExistsOnContract = chosenChannels?.some((channel) => channel.groupId === newGroupData.id);

        let newGroups = chosenGroups;
        let newChosenChannels = chosenChannels;

        // If channels exist on contract, save new, both for channels and the group.
        if (groupExistsOnContract) {
            newChosenChannels =
                chosenChannels?.map((channel) => {
                    const isTargetFormula =
                        groupChannels.some((item) => item.id === channel.channelId) &&
                        (channel.formatTypeId ?? null) === formatTypeId;
                    if (isTargetFormula) {
                        const fixedData = newGroupData.data.map((milestone: any) => {
                            return {
                                ...milestone,
                                percentage: milestone.percentage === "" ? 0 : +milestone.percentage,
                                quantity: milestone.quantity === "" ? 0 : +milestone.quantity,
                            };
                        });
                        return { ...channel, data: fixedData };
                    } else {
                        // Return others intact
                        return channel;
                    }
                }) || [];

            if (formatTypeId == null) {
                newGroups = chosenGroups.map((group) => {
                    if (group.id === newGroupData.id) {
                        const fixedData = newGroupData.data.map((milestone: any) => {
                            return {
                                ...milestone,
                                percentage: milestone.percentage === "" ? 0 : +milestone.percentage,
                                quantity: milestone.quantity === "" ? 0 : +milestone.quantity,
                            };
                        });
                        return { ...newGroupData, data: fixedData, synced: true };
                    } else {
                        return group;
                    }
                });
            }
        }

        // If channels don't exist add channels and group.
        if (!groupExistsOnContract) {
            const newChannelsWithData = groupChannels.map((channel) => {
                const newData = newGroupData.data.map((milestone: any) => {
                    return {
                        ...milestone,
                        percentage: milestone.percentage === "" ? 0 : +milestone.percentage,
                        quantity: milestone.quantity === "" ? 0 : +milestone.quantity,
                    };
                });
                return {
                    channelId: channel.id,
                    data: newData,
                    name: channel.name,
                    calculationBaseColumn: getDefaultCalculationBaseColumn(channel),
                    milestoneBaseColumn: getDefaultCalculationBaseColumn(channel),
                };
            });
            newChosenChannels = [...newChosenChannels, ...newChannelsWithData];
            newGroups = [...newGroups, newGroupData];
        }

        setChosenChannels(newChosenChannels);
        setChosenGroups(newGroups);
        onChange && onChange();
    };

    const handleAlignGroupOnSync = (group: GroupWithChannels) => {
        const referenceChannelId = group.channels[0]?.id;
        if (referenceChannelId == null) return;

        const groupChannelIds = group.channels.map((channel) => channel.id);
        const referenceDefault = chosenChannels.find(
            (row) => row.channelId === referenceChannelId && row.formatTypeId == null,
        );

        setChosenChannels((prev) =>
            alignCompensationGroupFormulasOnSync(prev, groupChannelIds, referenceChannelId, group.id),
        );
        setChosenGroups((prev) =>
            prev.map((item) =>
                item.id === group.id
                    ? {
                          ...item,
                          synced: true,
                          data: Array.isArray(referenceDefault?.data) ? referenceDefault.data : item.data,
                      }
                    : item,
            ),
        );
        onChange && onChange();
    };

    /**
     * Clean arrays when choosing template (especially needed when switching templates);
     */
    const cleanData = async () => {
        setActivatedChannels([]);
        setChosenChannels([]);
        setChosenGroups([]);
        setActiveGroups([]);
    };

    /**
     * Sorts channels/groups by active ones first and others below.
     * @param array Array of either channels or arrays.
     * @param activeItems Array with ids of channels/groups that are active.
     * @param setArray Function to set the update the array that is mapped.
     */
    const sortItemsByActive = async (array: IChannel[] | GroupWithChannels[], activeItems: number[], setArray: any) => {
        const updatedItems: any = array.map((item) => {
            if (activeItems.includes(item.id)) {
                return { ...item, active: true };
            } else {
                return { ...item, active: false };
            }
        });

        const sortedArray = sortArrayByBooleanProperty(updatedItems, "active");
        setArray(sortedArray);
    };

    /**
     * Injects template data into form once they've chosen a template.
     * @param template Template chosen to inject data.
     */
    const injectTemplateData = async (template: TermTemplate) => {
        await cleanData();
        const templateChannels = getTemplateChannels(template?.configuration, channels, {
            usesCompensation,
        });

        if (templateChannels) {
            setChosenChannels(templateChannels);

            const templateChannelsIDs = templateChannels.map((channel) => channel.channelId);
            setActivatedChannels(templateChannelsIDs);

            const templateGroups = getGroupWithChannels(channelCompensationGroups ?? [], templateChannels);
            const templateGroupsIDs = templateGroups.map((group) => group.id);
            setChosenGroups(templateGroups);
            setActiveGroups(templateGroupsIDs);

            const advancePayment = ((+template?.configuration.advancePayment || 0) / 100).toString();
            setValue && setValue("advancePayment", advancePayment);
            if (shouldSetPublishedBeforePeriodFromOpeningBalance(advancePayment)) {
                setValue && setValue("isTitlePublishedBeforePeriod", 1);
            }
            setValue && setValue("compensate", template.configuration.compensate);

            setUsesTemplate && setUsesTemplate(true);
            // Only issue here is that because I display channels and groups separate, they will be ordered within their section but groups will never be above channels...
            await sortItemsByActive(filteredChannels, templateChannelsIDs, setOrderedChannels);
            await sortItemsByActive(groupsWithChannels, templateGroupsIDs, setOrderedGroups);
            setModalType("");
        }
    };

    return (
        <>
            {from === "form" && (
                <>
                    <div className="channel-step-title">
                        <h3 className="form-section-title">
                            {" "}
                            {(indexStep ?? 0) + 1}. {stepTitle}
                        </h3>
                        {mode === "new" &&
                            ((termTemplates && termTemplates.length > 0) ||
                                (frameworkContracts && frameworkContracts.length > 0)) && (
                                <TextButton
                                    text={
                                        usesTemplate
                                            ? t("TermTemplates.ChangeTemplates")
                                            : t("TermTemplates.CompleteFromTemplate")
                                    }
                                    handleClick={() => setModalType("templates")}
                                    style={{ fontSize: "13px" }}
                                />
                            )}
                    </div>
                    <span className="form-section-description">{stepSubtitle}</span>
                </>
            )}
            <div className="channels">
                {!channels.length && (
                    <div className="no-channels-created">
                        <h3>{t("ConnectTitle.FirstChannelTitle")}</h3>
                        <p>{t("ConnectTitle.FirstChannelText")}</p>
                        {hasCreateChannelPermission && (
                            <StyledButton
                                type="button"
                                size="small"
                                buttonType="primary"
                                onClick={() => onAddChannel(true)}
                            >
                                <Plus />
                                {t("ConnectTitle.NewChannel")}
                            </StyledButton>
                        )}
                    </div>
                )}
                <ChannelsListSection
                    mode={mode}
                    activeChannels={activeChannelsList}
                    inactiveChannels={inactiveChannelsList}
                    activedChannels={activedChannels}
                    chosenChannels={chosenChannels}
                    activatedChannels={activatedChannels}
                    usesTemplate={usesTemplate}
                    handleChannelActivated={handleChannelActivated}
                    handleChannelUpdates={handleChannelUpdates}
                    handleToggleFormatTypeOverride={handleToggleFormatTypeOverride}
                    handleCalculationBaseColumnChange={handleCalculationBaseColumnChange}
                    handleMilestoneColumnChange={handleMilestoneColumnChange}
                    calculationBaseColumnLabels={calculationBaseColumnLabels}
                    formatTypesOnWork={formatTypesOnWork}
                    useFormData={{ setError, clearErrors }}
                    from={from}
                    onShowDefaultConfigurationChange={handleShowDefaultConfigurationChange}
                    canCreateChannel={hasCreateChannelPermission && channels.length > 0}
                    onAddChannel={() => onAddChannel(true)}
                    addChannelAboveList={from === "templates"}
                />
                {usesCompensation && (
                    <GroupsListSection
                        mode={mode}
                        activeGroups={activeGroupsList}
                        inactiveGroups={inactiveGroupsList}
                        activedChannels={activedChannels}
                        chosenChannels={chosenChannels}
                        chosenGroups={chosenGroups}
                        activeGroupsIds={activeGroups}
                        setChosenGroups={setChosenGroups}
                        usesTemplate={usesTemplate}
                        onChange={onChange}
                        handleChannelUpdates={handleChannelUpdates}
                        handleToggleFormatTypeOverride={handleToggleFormatTypeOverride}
                        handleGroupCalculationBaseColumnChange={handleGroupCalculationBaseColumnChange}
                        handleGroupMilestoneColumnChange={handleGroupMilestoneColumnChange}
                        handleToggleGroupFormatTypeOverride={handleToggleGroupFormatTypeOverride}
                        calculationBaseColumnLabels={calculationBaseColumnLabels}
                        formatTypesOnWork={formatTypesOnWork}
                        from={from}
                        onShowDefaultConfigurationChange={handleShowDefaultConfigurationChange}
                        onGroupShowDefaultConfigurationChange={handleGroupShowDefaultConfigurationChange}
                        handleGroupActivation={handleGroupActivation}
                        handleGroupUpdate={handleGroupUpdate}
                        handleAlignGroupOnSync={handleAlignGroupOnSync}
                        useFormData={{ setError, clearErrors }}
                    />
                )}
                {hasCreateChannelPermission &&
                    channels.length > 0 &&
                    from !== "templates" &&
                    !(mode === "edit" && inactiveChannelsList.length > 0) && (
                        <AddChannelButton
                            label={t("ConnectTitle.NewChannel")}
                            onClick={() => onAddChannel(true)}
                            align="start"
                            className="channels-step-add-channel"
                        />
                    )}
            </div>
            {from === "form" && <TermCustomFields section="channels" mode={mode === "edit" ? "edit" : "new"} />}
            <TemplatesModal
                open={modalType === "templates"}
                onClose={() => setModalType("")}
                templates={termTemplates}
                frameworkContracts={frameworkContracts}
                onSubmit={injectTemplateData}
            />
        </>
    );
};

export default ChannelsStep;
